import React, { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import axios from "axios";
import DotPulseLoading from "components/Loading/DotPulseLoading";
import CustomSnackbar from "components/CustomSnackbar";
import AuthNavbar from "components/AuthNavbar";
import LanguageSelector from "components/LanguageSelector";

// const fadeIn = {
//   hidden: { opacity: 0, y: 20 },
//   visible: { opacity: 1, y: 0 },
// };

function LoginSso() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 968);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const { t } = useTranslation();

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 968);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const lang = localStorage.getItem("i18nextLng") || "en";
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/sso`,
        {
          email,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept-Language": lang,
          },
        }
      );
      setSnackbar({
        open: true,
        message: response.data.message || "successfully !",
        severity: "success",
      });
      setSuccess(true);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Reset failed",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AuthNavbar />
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "#ffffff",
          display: "flex",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.8); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes floatSlow {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(15px, -15px); }
        }
      `}</style>

        {/* Loading Overlay */}
        {loading && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DotPulseLoading size={14} color="#388e3c" />
          </div>
        )}

        {/* Abstract Bubble Background - Left Side (Green Theme) */}
        <div
          style={{
            position: "absolute",
            left: "-25%",
            top: "40%",
            transform: "translateY(-50%)",
            width: isMobile ? "150%" : "85%",
            height: "150%",
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          <svg
            viewBox="0 0 800 1000"
            style={{
              width: "100%",
              height: "100%",
              filter: "drop-shadow(0 10px 40px rgba(56, 142, 60, 0.15))",
            }}
          >
            <defs>
              <linearGradient
                id="gradientSSO"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  style={{ stopColor: "#e8f5e9", stopOpacity: 1 }}
                />
                <stop
                  offset="50%"
                  style={{ stopColor: "#c8e6c9", stopOpacity: 1 }}
                />
                <stop
                  offset="100%"
                  style={{ stopColor: "#a5d6a7", stopOpacity: 1 }}
                />
              </linearGradient>
            </defs>
            <path
              d="M 150,100 C 150,50 200,20 280,50 C 360,80 400,120 450,200 C 500,280 520,360 480,450 C 440,540 400,600 350,680 C 300,760 250,820 180,860 C 110,900 50,880 30,800 C 10,720 -20,640 20,540 C 60,440 100,360 120,260 C 140,160 150,150 150,100 Z"
              fill="url(#gradientSSO)"
              style={{ animation: "float 8s ease-in-out infinite" }}
            />
          </svg>
          <div
            style={{
              position: "absolute",
              top: "15%",
              right: "10%",
              width: "120px",
              height: "120px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, rgba(165, 214, 167, 0.4), rgba(56, 142, 60, 0.2))",
              animation: "floatSlow 6s ease-in-out infinite",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: "20%",
              right: "25%",
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, rgba(200, 230, 201, 0.5), rgba(165, 214, 167, 0.3))",
              animation: "floatSlow 7s ease-in-out infinite reverse",
            }}
          />
        </div>

        {/* Right Bottom Bubble */}
        <div
          style={{
            position: "absolute",
            right: "-25%",
            bottom: "10%",
            width: isMobile ? "150%" : "85%",
            height: "150%",
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          <svg
            viewBox="0 0 800 1000"
            style={{
              width: "100%",
              height: "100%",
              filter: "drop-shadow(0 10px 40px rgba(56, 142, 60, 0.15))",
              opacity: 0.2,
            }}
          >
            <defs>
              <linearGradient
                id="gradientSSO2"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  style={{ stopColor: "#e8f5e9", stopOpacity: 1 }}
                />
                <stop
                  offset="50%"
                  style={{ stopColor: "#c8e6c9", stopOpacity: 1 }}
                />
                <stop
                  offset="100%"
                  style={{ stopColor: "#a5d6a7", stopOpacity: 1 }}
                />
              </linearGradient>
            </defs>
            <path
              d="M 150,100 C 150,50 200,20 280,50 C 360,80 900,120 450,200 C 500,280 520,360 480,450 C 440,540 400,600 350,680 C 300,760 250,820 180,860 C 110,900 50,880 30,800 C 10,720 -20,640 20,540 C 60,440 100,360 120,260 C 140,160 150,150 150,100 Z"
              fill="url(#gradientSSO2)"
              style={{ animation: "float 8s ease-in-out infinite" }}
            />
          </svg>
        </div>

        {/* Main Form Container */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? "32px 24px" : "48px",
            position: "relative",
            zIndex: 1,
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            style={{ width: "100%", maxWidth: "480px" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "24px",
                position: "relative",
                zIndex: 20,
              }}
            >
              <LanguageSelector />
            </div>
            {success ? (
              <>
                <h1
                  style={{
                    fontSize: isMobile ? "32px" : "40px",
                    fontWeight: "bold",
                    color: "#1a202c",
                    marginBottom: "12px",
                    textAlign: "center",
                  }}
                >
                  {t("login.sso.successTitle")}
                </h1>
                <p
                  style={{
                    fontSize: "16px",
                    color: "#718096",
                    textAlign: "center",
                    marginBottom: "40px",
                  }}
                >
                  {t("login.sso.successMessage")} {email}{" "}
                  {t("login.sso.successDescription")}
                </p>
                <motion.a
                  href="/login"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  style={{
                    display: "block",
                    width: "100%",
                    padding: "16px",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "white",
                    backgroundColor: "#388e3c",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    textAlign: "center",
                    textDecoration: "none",
                    transition: "all 0.3s ease",
                  }}
                >
                  {t("login.sso.backButton")}
                </motion.a>
              </>
            ) : (
              <>
                <h1
                  style={{
                    fontSize: isMobile ? "32px" : "40px",
                    fontWeight: "bold",
                    color: "#1a202c",
                    marginBottom: "12px",
                    textAlign: "center",
                  }}
                >
                  {t("login.sso.title")}
                </h1>
                <p
                  style={{
                    fontSize: "16px",
                    color: "#718096",
                    textAlign: "center",
                    marginBottom: "40px",
                  }}
                >
                  {t("login.sso.subtitle")}
                </p>

                <form onSubmit={handleSubmit}>
                  {/* Email Input */}
                  <div style={{ marginBottom: "24px" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#4a5568",
                        marginBottom: "8px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="user@mail.com"
                      style={{
                        width: "100%",
                        padding: "14px 16px",
                        fontSize: "15px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        backgroundColor: "#ffffff",
                        transition: "all 0.3s ease",
                        outline: "none",
                        fontFamily: "inherit",
                        boxSizing: "border-box",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#388e3c";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(56, 142, 60, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#d1d5db";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={loading}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    style={{
                      width: "100%",
                      padding: "16px",
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "white",
                      backgroundColor: "#388e3c",
                      border: "none",
                      borderRadius: "8px",
                      cursor: loading ? "not-allowed" : "pointer",
                      opacity: loading ? 0.7 : 1,
                      marginBottom: "24px",
                      fontFamily: "inherit",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {loading ? "Signing in..." : t("login.sso.signInButton")}
                  </motion.button>

                  {/* Back to Login */}
                  {/* <div style={{ textAlign: "center" }}>
                    <a
                      href="/login"
                      style={{
                        color: "#388e3c",
                        textDecoration: "none",
                        fontWeight: "500",
                        fontSize: "14px",
                      }}
                    >
                      {t("login.sso.loginButton")}
                    </a>
                  </div> */}
                </form>
              </>
            )}
          </motion.div>
        </div>

        {/* Snackbar */}
        <CustomSnackbar
          open={snackbar.open}
          onClose={handleCloseSnackbar}
          message={snackbar.message}
          severity={snackbar.severity}
        />
      </div>
    </>
  );
}

export default LoginSso;
